import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PROGRAMS = [
  { slug: 'e-personalidade', name: 'E-Personalidade' },
  { slug: 'e-processual', name: 'E-Processual' },
  { slug: 'e-corpo', name: 'E-Corpo' },
  { slug: 'e-vendas', name: 'E-Vendas' },
  { slug: 'e-got', name: 'E-GOT' },
];

const MODALITIES = ['presencial', 'online', 'intensivo', 'extensivo'];
const CLASS_STATUS = ['aberta', 'encerrada', 'lotada', 'cancelada'];

const EMPTY_FORM = {
  title: '',
  description: '',
  product_id: '',
  modality: 'presencial',
  event_date: '',
  end_date: '',
  event_time: '',
  city: '',
  state: '',
  franchise_id: '',
  location: '',
  seats_total: '',
  seats_remaining: '',
  investment: '',
  class_status: 'aberta',
  notes: '',
  link: '',
  registration_url: '',
  image: '',
  event_type: 'presencial',
};

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsRes, franchisesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/events`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/franchises`, { withCredentials: true }),
      ]);
      setEvents(eventsRes.data);
      setFranchises(franchisesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelect = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // When admin picks a franchise, prefill city/state from it
      if (name === 'franchise_id' && value) {
        const fr = franchises.find((f) => f.franchise_id === value);
        if (fr) {
          if (!prev.city) next.city = fr.city || '';
          if (!prev.state) next.state = fr.state || '';
        }
      }
      // Mirror modality → event_type for backward compat
      if (name === 'modality') {
        next.event_type =
          value === 'online' ? 'online' : value === 'presencial' ? 'presencial' : prev.event_type;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        product_id: formData.product_id || null,
        modality: formData.modality,
        event_type: formData.event_type || formData.modality,
        event_date: new Date(formData.event_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        event_time: formData.event_time,
        city: formData.city || null,
        state: formData.state || null,
        franchise_id: formData.franchise_id || null,
        location: formData.location || formData.city || '',
        seats_total: formData.seats_total ? parseInt(formData.seats_total, 10) : null,
        seats_remaining:
          formData.seats_remaining !== ''
            ? parseInt(formData.seats_remaining, 10)
            : formData.seats_total
            ? parseInt(formData.seats_total, 10)
            : null,
        investment: formData.investment ? parseFloat(formData.investment) : null,
        class_status: formData.class_status,
        notes: formData.notes || null,
        link: formData.link || null,
        registration_url: formData.registration_url || null,
        image: formData.image || null,
      };

      if (editingEvent) {
        await axios.put(`${API_URL}/admin/events/${editingEvent.event_id}`, payload, {
          withCredentials: true,
        });
        toast.success('Turma atualizada!');
      } else {
        await axios.post(`${API_URL}/admin/events`, payload, { withCredentials: true });
        toast.success('Turma criada!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar turma');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      product_id: event.product_id || '',
      modality: event.modality || event.event_type || 'presencial',
      event_date: event.event_date ? event.event_date.split('T')[0] : '',
      end_date: event.end_date ? event.end_date.split('T')[0] : '',
      event_time: event.event_time || '',
      city: event.city || '',
      state: event.state || '',
      franchise_id: event.franchise_id || '',
      location: event.location || '',
      seats_total: event.seats_total != null ? String(event.seats_total) : '',
      seats_remaining: event.seats_remaining != null ? String(event.seats_remaining) : '',
      investment: event.investment != null ? String(event.investment) : '',
      class_status: event.class_status || 'aberta',
      notes: event.notes || '',
      link: event.link || '',
      registration_url: event.registration_url || '',
      image: event.image || '',
      event_type: event.event_type || event.modality || 'presencial',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta turma?')) return;
    try {
      await axios.delete(`${API_URL}/admin/events/${eventId}`, { withCredentials: true });
      toast.success('Turma excluída!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir turma');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingEvent(null);
  };

  const programLabel = (slug) => PROGRAMS.find((p) => p.slug === slug)?.name || '—';
  const franchiseLabel = (id) => {
    const f = franchises.find((x) => x.franchise_id === id);
    return f ? f.name || `${f.city} - ${f.state}` : '—';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold mb-2">Turmas (Agenda)</h2>
          <p className="text-neutral-600">Gerencie próximas turmas dos programas IE</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          data-testid="admin-new-turma-btn"
          className="flex items-center gap-2"
        >
          <iconify-icon icon="solar:calendar-add-linear"></iconify-icon>
          Nova Turma
        </Button>
      </div>

      {/* Turmas List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <div
            key={event.event_id}
            className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-lg transition-shadow"
            data-testid={`admin-turma-row-${event.event_id}`}
          >
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {event.product_id && (
                  <span className="px-2 py-0.5 bg-accent-orange text-white rounded-full text-[10px] font-bold uppercase tracking-wider">
                    {programLabel(event.product_id)}
                  </span>
                )}
                {(event.modality || event.event_type) && (
                  <span className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                    {event.modality || event.event_type}
                  </span>
                )}
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    event.class_status === 'lotada'
                      ? 'bg-red-100 text-red-700'
                      : event.class_status === 'cancelada'
                      ? 'bg-neutral-200 text-neutral-600'
                      : event.class_status === 'encerrada'
                      ? 'bg-neutral-100 text-neutral-500'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {event.class_status || 'aberta'}
                </span>
              </div>

              <h3 className="font-bold text-lg mb-2 line-clamp-2">{event.title}</h3>
              <p className="text-sm text-neutral-600 mb-3 line-clamp-2">{event.description}</p>

              <div className="space-y-1 text-sm text-neutral-500 mb-4">
                <p className="flex items-center gap-2">
                  <iconify-icon icon="solar:calendar-linear"></iconify-icon>
                  {new Date(event.event_date).toLocaleDateString('pt-BR')}
                  {event.end_date && ` → ${new Date(event.end_date).toLocaleDateString('pt-BR')}`}
                </p>
                <p className="flex items-center gap-2">
                  <iconify-icon icon="solar:clock-circle-linear"></iconify-icon>
                  {event.event_time}
                </p>
                <p className="flex items-center gap-2">
                  <iconify-icon icon="solar:map-point-linear"></iconify-icon>
                  {event.city || event.location}
                  {event.state && ` · ${event.state}`}
                </p>
                {event.franchise_id && (
                  <p className="flex items-center gap-2">
                    <iconify-icon icon="solar:shop-linear"></iconify-icon>
                    {franchiseLabel(event.franchise_id)}
                  </p>
                )}
                {event.seats_total != null && (
                  <p className="flex items-center gap-2">
                    <iconify-icon icon="solar:users-group-rounded-linear"></iconify-icon>
                    {event.seats_remaining ?? 0} / {event.seats_total} vagas
                  </p>
                )}
                {typeof event.investment === 'number' && event.investment > 0 && (
                  <p className="flex items-center gap-2 font-semibold text-black">
                    <iconify-icon icon="solar:tag-price-linear"></iconify-icon>
                    R$ {event.investment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(event)}
                  data-testid={`admin-turma-edit-${event.event_id}`}
                  className="flex-1 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 rounded-lg text-sm font-medium transition-colors"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(event.event_id)}
                  data-testid={`admin-turma-delete-${event.event_id}`}
                  className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                >
                  <iconify-icon icon="solar:trash-bin-linear" className="text-xl"></iconify-icon>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {events.length === 0 && (
        <div className="text-center py-12 text-neutral-500 bg-white rounded-xl border border-neutral-200">
          <iconify-icon icon="solar:calendar-linear" className="text-6xl mb-4"></iconify-icon>
          <p>Nenhuma turma cadastrada ainda</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Editar Turma' : 'Nova Turma'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  name="title"
                  data-testid="admin-form-title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="description">Descrição *</Label>
                <textarea
                  id="description"
                  name="description"
                  data-testid="admin-form-description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full border border-neutral-300 rounded-md p-2 min-h-[80px]"
                  required
                />
              </div>

              <div>
                <Label>Programa *</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(v) => handleSelect('product_id', v)}
                >
                  <SelectTrigger data-testid="admin-form-program">
                    <SelectValue placeholder="Selecione um programa" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROGRAMS.map((p) => (
                      <SelectItem key={p.slug} value={p.slug}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Modalidade *</Label>
                <Select
                  value={formData.modality}
                  onValueChange={(v) => handleSelect('modality', v)}
                >
                  <SelectTrigger data-testid="admin-form-modality">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODALITIES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="event_date">Data de início *</Label>
                <Input
                  id="event_date"
                  name="event_date"
                  data-testid="admin-form-start-date"
                  type="date"
                  value={formData.event_date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="end_date">Data de fim</Label>
                <Input
                  id="end_date"
                  name="end_date"
                  data-testid="admin-form-end-date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="event_time">Horário *</Label>
                <Input
                  id="event_time"
                  name="event_time"
                  data-testid="admin-form-time"
                  placeholder="Ex: 8h às 12h, ou Sábados 9h–13h"
                  value={formData.event_time}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="city">Cidade</Label>
                <Input
                  id="city"
                  name="city"
                  data-testid="admin-form-city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="state">Estado (ex: RS)</Label>
                <Input
                  id="state"
                  name="state"
                  data-testid="admin-form-state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="RS / SC / PR..."
                />
              </div>
              <div className="md:col-span-2">
                <Label>Franquia responsável</Label>
                <Select
                  value={formData.franchise_id}
                  onValueChange={(v) => handleSelect('franchise_id', v)}
                >
                  <SelectTrigger data-testid="admin-form-franchise">
                    <SelectValue placeholder="Selecione uma franquia" />
                  </SelectTrigger>
                  <SelectContent>
                    {franchises.map((f) => (
                      <SelectItem key={f.franchise_id} value={f.franchise_id}>
                        {f.name || `${f.city} - ${f.state}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="seats_total">Vagas totais</Label>
                <Input
                  id="seats_total"
                  name="seats_total"
                  data-testid="admin-form-seats-total"
                  type="number"
                  min="0"
                  value={formData.seats_total}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="seats_remaining">
                  Vagas restantes <span className="text-xs text-neutral-500">(0 &rarr; vira &quot;lotada&quot;)</span>
                </Label>
                <Input
                  id="seats_remaining"
                  name="seats_remaining"
                  data-testid="admin-form-seats-remaining"
                  type="number"
                  min="0"
                  value={formData.seats_remaining}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="investment">Investimento (R$)</Label>
                <Input
                  id="investment"
                  name="investment"
                  data-testid="admin-form-investment"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.investment}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.class_status}
                  onValueChange={(v) => handleSelect('class_status', v)}
                >
                  <SelectTrigger data-testid="admin-form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASS_STATUS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="registration_url" className="flex items-center gap-2">
                  <iconify-icon icon="solar:ticket-linear" className="text-accent-orange"></iconify-icon>
                  Link do formulário de inscrição
                </Label>
                <Input
                  id="registration_url"
                  name="registration_url"
                  type="url"
                  data-testid="admin-form-registration-url"
                  value={formData.registration_url}
                  onChange={handleChange}
                  placeholder="https://forms.gle/... ou https://ieneagrama.com.br/inscricao/..."
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Se preenchido, o botão "Inscreva-se" na Agenda leva direto pra este link (tem prioridade sobre WhatsApp da franquia).
                </p>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="link">Link de aula online (Zoom/Meet)</Label>
                <Input
                  id="link"
                  name="link"
                  type="url"
                  value={formData.link}
                  onChange={handleChange}
                  placeholder="https://zoom.us/..."
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="image">Imagem (URL)</Label>
                <Input id="image" name="image" value={formData.image} onChange={handleChange} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="notes">Observações</Label>
                <textarea
                  id="notes"
                  name="notes"
                  data-testid="admin-form-notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="w-full border border-neutral-300 rounded-md p-2 min-h-[60px]"
                  placeholder="Informações extras: local exato, dress code, material incluso..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="admin-form-submit">
                {editingEvent ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsManager;
